import React from 'react';
import { v4 as UUID } from 'uuid';
import {
  CustomInput,
  CustomSelect,
  CustomTextarea,
  CustomToggle,
} from '../Components/CustomFormElements';
import Button from '../Components/Buttons';

function FragmentParameters({
  parameters,
  setParameters,
  isDisabled,
  isNested = false,
  idPrefix = 'parameter',
}) {
  const parameterTypeOptions = [
    { value: 'str', label: 'String' },
    { value: 'int', label: 'Integer' },
    { value: 'float', label: 'Float' },
    { value: 'bool', label: 'Boolean' },
    { value: 'list', label: 'List' },
    { value: 'json', label: 'JSON object' },
  ];

  const itemTypeOptions = parameterTypeOptions.filter((option) => option.value !== 'list');

  const addParameter = () => {
    setParameters([
      ...parameters,
      {
        key: UUID(),
        name: '',
        description: '',
        type: 'str',
      },
    ]);
  };

  const removeParameter = (index) => {
    setParameters(parameters.filter((_, parameterIndex) => parameterIndex !== index));
  };

  const updateParameter = (index, field, value) => {
    const newParameters = [...parameters];
    newParameters[index] = { ...newParameters[index], [field]: value };
    setParameters(newParameters);
  };

  return (
    <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
      <h4 className="h4-title">{isNested ? 'Fields' : 'Variable List'}</h4>
      <div className="small-font mb-2">
        {isNested
          ? 'Define the fields of this object'
          : 'Define the values the agent must provide for this fragment'}
      </div>

      {parameters.map((parameter, index) => (
        <div key={parameter.key || index} className="attribute-row mb-2 p-2 border-yes radius-yes">
          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Name</div>
              <div className="small-font">
                {isNested
                  ? 'Field name in the JSON object'
                  : 'Placeholder name used in the template'}
              </div>
            </div>
            <div className="align-wrapper d-flex align-center gap">
              <CustomInput
                id={`${idPrefix}-name-${index}`}
                label="Name"
                placeholder="Enter name"
                type="text"
                value={parameter.name}
                onChange={(id, value) => updateParameter(index, 'name', value)}
                disabled={isDisabled}
              />
              {!isDisabled && (
                <Button variant="danger" size="small" onClick={() => removeParameter(index)}>
                  <i className="fa-solid fa-trash"></i>
                </Button>
              )}
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Type</div>
              <div className="small-font">Select variable type</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomSelect
                id={`${idPrefix}-type-${index}`}
                label="Type"
                options={parameterTypeOptions}
                value={parameter.type || 'str'}
                onChange={(value) => updateParameter(index, 'type', value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          {isNested && (
            <div className="input-block d-flex flex-row justify-between">
              <div className="input-description">
                <div className="input-title">Required</div>
                <div className="small-font">The agent must always provide this field</div>
              </div>
              <div className="align-wrapper d-flex align-center">
                <CustomToggle
                  id={`${idPrefix}-required-${index}`}
                  label={parameter.required === false ? 'Optional' : 'Required'}
                  checked={parameter.required !== false}
                  onChange={(id, value) => updateParameter(index, 'required', value)}
                  disabled={isDisabled}
                />
              </div>
            </div>
          )}

          {parameter.type === 'list' && (
            <div className="input-block d-flex flex-row justify-between">
              <div className="input-description">
                <div className="input-title">Item type</div>
                <div className="small-font">Type of each list item</div>
              </div>
              <div className="align-wrapper d-flex align-center">
                <CustomSelect
                  id={`${idPrefix}-item-type-${index}`}
                  label="Item type"
                  options={itemTypeOptions}
                  value={parameter.items?.type || ''}
                  onChange={(value) =>
                    updateParameter(index, 'items', { ...parameter.items, type: value })
                  }
                  disabled={isDisabled}
                />
              </div>
            </div>
          )}

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Description</div>
              <div className="small-font">Explain to the agent what value to provide</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomTextarea
                id={`${idPrefix}-description-${index}`}
                label="Description"
                name={`${idPrefix}-description-${index}`}
                type="text"
                addedText={parameter.description || ''}
                onChange={(value) => updateParameter(index, 'description', value)}
                resizable={false}
                disabled={isDisabled}
                height="80px"
              />
            </div>
          </div>

          {parameter.type === 'list' && parameter.items?.type === 'json' && (
            <FragmentParameters
              parameters={parameter.items.properties || []}
              setParameters={(properties) =>
                updateParameter(index, 'items', { ...parameter.items, properties })
              }
              isDisabled={isDisabled}
              isNested
              idPrefix={`${idPrefix}-${index}-item`}
            />
          )}

          {parameter.type === 'json' && (
            <FragmentParameters
              parameters={parameter.properties || []}
              setParameters={(properties) => updateParameter(index, 'properties', properties)}
              isDisabled={isDisabled}
              isNested
              idPrefix={`${idPrefix}-${index}-field`}
            />
          )}
        </div>
      ))}

      {!isDisabled && (
        <Button variant="secondary" size="medium" onClick={addParameter}>
          <i className="fa-solid fa-plus"></i> {isNested ? 'Add Field' : 'Add Variable'}
        </Button>
      )}
    </div>
  );
}

export default FragmentParameters;
